import { useMemo, useState, useEffect, useRef, type FormEvent } from "react";
import type { EventItem } from "@site-factory/config";
import type { EventBookingSectionContent, EventBookingRequest } from "@site-factory/config";
import { cn } from "../utils";
import { SectionTitle, SectionSubtitle } from "../typography/Typography";
import { Card, CardContent } from "../components/Card";
import { Button } from "../components/Button";
import { AnimatedIcon } from "../components/AnimatedIcon";
import { Icon, iconNames, type IconName } from "../icons";
import { BandedSection, type BandedSectionVariant } from "../components/BandedSection";
import type { EmailJsConfig } from "../components/ContactFormPresets";
import { showToast } from "../components/Toast";

export interface EventBookingSectionProps extends EventBookingSectionContent {
  className?: string;
  bandVariant?: BandedSectionVariant;
  availabilityEvents?: Array<
    Pick<EventItem, "date" | "time"> & { start?: string; end?: string; blocked?: boolean }
  >;
  emailJsConfig?: EmailJsConfig | null;
  apiConfig?: {
    enabled?: boolean;
    baseUrl?: string;
    businessSlug?: string;
    turnstileSiteKey?: string;
  } | null;
  initialDate?: string;
  initialEventType?: string;
}

export function EventBookingSection({
  title,
  subtitle,
  emailTo,
  eventTypes,
  ctaText = "Request Booking",
  successMessage = "Thank you! We'll get back to you soon about your event booking.",
  confirmationHref,
  confirmationLabel = "View booking confirmation",
  variant = "simple",
  emailJsTemplateId,
  className,
  bandVariant = "plain",
  availabilityEvents = [],
  emailJsConfig: emailJsConfigOverride,
  apiConfig,
  initialDate,
  initialEventType,
}: EventBookingSectionProps) {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState<"idle" | "sending" | "error">("idle");
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<EventBookingRequest>>({
    eventType: initialEventType || eventTypes[0]?.label || "",
    publicVisibility: "private",
    date: initialDate || "",
  });
  const stepTopRef = useRef<HTMLDivElement | null>(null);
  const previousStepRef = useRef(step);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [contactErrors, setContactErrors] = useState<{ name?: boolean; email?: boolean; phone?: boolean }>({});
  const [eventErrors, setEventErrors] = useState<{
    eventType?: boolean;
    date?: boolean;
    time?: boolean;
    location?: boolean;
    guestCount?: boolean;
    powerAccess?: boolean;
  }>({});

  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  const normalizePhone = (value: string) => value.replace(/\D/g, "");
  const isPhoneValid = (value: string | undefined | null) => {
    if (!value) return false;
    const digits = normalizePhone(value);
    return digits.length === 10 && digits[0] !== "0";
  };
  const isEmailValid = (value: string | undefined | null) => !!value && emailRegex.test(value);
  const isGuestCountValid =
    formData.guestCount !== undefined &&
    formData.guestCount !== null &&
    !Number.isNaN(formData.guestCount) &&
    formData.guestCount >= 1;

  const emailJsConfig = useMemo(() => {
    if (emailJsConfigOverride) return emailJsConfigOverride;
    if (!emailJsTemplateId) return null;
    // Support Vite-style or NEXT_PUBLIC_* env vars
    const env = (typeof import.meta !== "undefined" ? (import.meta as any).env : {}) as Record<string, string>;
    const serviceId = env.VITE_EMAILJS_SERVICE_ID || env.NEXT_PUBLIC_EMAILJS_SERVICE_ID;
    const publicKey = env.VITE_EMAILJS_PUBLIC_KEY || env.NEXT_PUBLIC_EMAILJS_PUBLIC_KEY;
    if (!serviceId || !publicKey) return null;
    return { serviceId, publicKey, templateId: emailJsTemplateId };
  }, [emailJsConfigOverride, emailJsTemplateId]);

  const opsApiConfig = useMemo(() => {
    if (apiConfig) return apiConfig;
    const env = (typeof import.meta !== "undefined" ? (import.meta as any).env : {}) as Record<string, string>;
    const enabled = env.VITE_PATCHROOT_OPS_ENABLED === "true";
    const baseUrl = env.VITE_PATCHROOT_OPS_API_BASE;
    const baseDomain = "patchroot.com";
    const slugOverride = env.VITE_PATCHROOT_OPS_BUSINESS_SLUG;
    const inferSlug = () => {
      if (typeof window === "undefined") return undefined;
      const host = window.location.hostname.toLowerCase();
      if (host === baseDomain) return undefined;
      if (host.endsWith(`.${baseDomain}`)) {
        const sub = host.slice(0, -1 * (baseDomain.length + 1));
        if (!sub || sub.startsWith("admin.")) return undefined;
        return sub.split(".")[0];
      }
      return undefined;
    };
    const turnstileSiteKey = env.VITE_TURNSTILE_SITE_KEY;
    return { enabled, baseUrl, businessSlug: slugOverride || inferSlug(), turnstileSiteKey };
  }, [apiConfig]);

  const opsApiEnabled = Boolean(opsApiConfig?.enabled && opsApiConfig?.baseUrl);
  const turnstileEnabled = Boolean(opsApiEnabled && opsApiConfig?.turnstileSiteKey);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileNonce, setTurnstileNonce] = useState(0);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!turnstileEnabled) return;
    const scriptId = "patchroot-turnstile-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const renderWidget = () => {
      const siteKey = opsApiConfig?.turnstileSiteKey;
      if (!siteKey || !(window as any).turnstile) return;
      const container = turnstileContainerRef.current;
      if (!container || turnstileWidgetIdRef.current) return;
      turnstileWidgetIdRef.current = (window as any).turnstile.render(container, {
        sitekey: siteKey,
        callback: (token: string) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(null),
        "error-callback": () => setTurnstileToken(null),
        "refresh-expired": "auto",
      });
    };

    const interval = window.setInterval(() => {
      if ((window as any).turnstile) {
        window.clearInterval(interval);
        renderWidget();
      }
    }, 200);

    return () => {
      window.clearInterval(interval);
      if (!turnstileContainerRef.current) {
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [turnstileEnabled, opsApiConfig?.turnstileSiteKey, step, turnstileNonce]);
  useEffect(() => {
    if (initialEventType && eventTypes.some((type) => type.label === initialEventType) && !formData.eventType) {
      setFormData((prev) => ({ ...prev, eventType: initialEventType }));
    }
  }, [initialEventType, eventTypes, formData.eventType]);

  useEffect(() => {
    if (initialDate && !formData.date) {
      setFormData((prev) => ({ ...prev, date: initialDate }));
    }
  }, [initialDate, formData.date]);

  useEffect(() => {
    if (previousStepRef.current === step) {
      return;
    }
    previousStepRef.current = step;
    if (!stepTopRef.current) return;
    requestAnimationFrame(() => {
      stepTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      const focusable = stepTopRef.current?.querySelector<HTMLElement>(
        "input, textarea, select, button"
      );
      focusable?.focus();
    });
  }, [step]);

  // Generate time options (9 AM - 11 PM in 30-minute increments)
  const generateTimeOptions = () => {
    const times: string[] = [];
    for (let hour = 9; hour <= 23; hour++) {
      for (let minute of [0, 30]) {
        if (hour === 23 && minute === 30) break; // Stop at 11:00 PM
        const period = hour >= 12 ? "PM" : "AM";
        const displayHour = hour > 12 ? hour - 12 : hour;
        const displayMinute = minute.toString().padStart(2, "0");
        times.push(`${displayHour}:${displayMinute} ${period}`);
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  const isTimeRangeValid = () => {
    if (!startTime || !endTime) return false;
    const startIdx = timeOptions.indexOf(startTime);
    const endIdx = timeOptions.indexOf(endTime);
    return startIdx !== -1 && endIdx !== -1 && startIdx < endIdx;
  };

  const buildEventErrors = () => ({
    eventType: !formData.eventType,
    date: !formData.date,
    time: !startTime || !endTime || !isTimeRangeValid(),
    location: !formData.location,
    guestCount: !isGuestCountValid,
    powerAccess: !formData.powerAccess,
  });

  const buildContactErrors = () => ({
    name: !formData.name,
    email: !isEmailValid(formData.email),
    phone: !isPhoneValid(formData.phone),
  });

  const updateField = (field: keyof EventBookingRequest, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "name" || field === "email" || field === "phone") {
      setContactErrors((prev) => ({ ...prev, [field]: false }));
    }
    if (
      field === "eventType" ||
      field === "date" ||
      field === "time" ||
      field === "location" ||
      field === "guestCount" ||
      field === "powerAccess"
    ) {
      setEventErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  // Update time when start/end times change
  const updateTime = (start: string, end: string) => {
    if (start && end) {
      updateField("time", `${start} - ${end}`);
    } else if (start) {
      updateField("time", start);
    }
  };

  const parseTimeToMinutes = (value?: string) => {
    if (!value) return null;
    const trimmed = value.trim();
    const hhmm = trimmed.match(/^(\d{1,2}):(\d{2})$/);
    if (hhmm) {
      const h = parseInt(hhmm[1], 10);
      const m = parseInt(hhmm[2], 10);
      return h * 60 + m;
    }
    const ampm = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (ampm) {
      let h = parseInt(ampm[1], 10);
      const m = parseInt(ampm[2], 10);
      const isPM = ampm[3].toUpperCase() === "PM";
      if (h === 12) h = 0;
      return h * 60 + m + (isPM ? 12 * 60 : 0);
    }
    return null;
  };

  const getBusyIntervalsForDate = (dateStr?: string) => {
    if (!dateStr) return [];
    return (availabilityEvents || [])
      .filter((e) => e.date === dateStr)
      .map((e) => {
        const start = parseTimeToMinutes((e as any).start) || parseTimeToMinutes(e.time?.split("-")[0]?.trim());
        const end = parseTimeToMinutes((e as any).end) || parseTimeToMinutes(e.time?.split("-")[1]?.trim());
        return start !== null && end !== null && end > start ? [start, end] : null;
      })
      .filter(Boolean) as Array<[number, number]>;
  };

  const requestConflictsWithAvailability = () => {
    if (!formData.date || !startTime) return false;
    const reqStart = parseTimeToMinutes(startTime);
    const reqEnd = parseTimeToMinutes(endTime) || (reqStart !== null ? reqStart + 60 : null);
    if (reqStart === null || reqEnd === null) return false;

    const busy = getBusyIntervalsForDate(formData.date);
    // simple overlap check
    return busy.some(([s, e]) => !(reqEnd <= s || reqStart >= e));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Basic validation for email and phone
    if (formData.email && !isEmailValid(formData.email)) {
      showToast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "error" });
      return;
    }
    if (formData.phone && !isPhoneValid(formData.phone)) {
      showToast({ title: "Invalid phone", description: "Please enter a valid phone number.", variant: "error" });
      return;
    }
    if (!isGuestCountValid) {
      showToast({
        title: "Guest count required",
        description: "Please enter a valid guest count of at least 1.",
        variant: "error",
      });
      return;
    }
    if (!formData.powerAccess) {
      showToast({
        title: "Event details required",
        description: "Please select power access.",
        variant: "error",
      });
      return;
    }

    // Construct email
    const subject = `Event Booking Request: ${formData.eventType}`;
    const body = `
Event Booking Request
=====================

Event Type: ${formData.eventType}
Public Listing: ${formData.publicVisibility || "private"}
${formData.publicTitle ? `Public Title: ${formData.publicTitle}` : ""}
${formData.publicDescription ? `Public Description: ${formData.publicDescription}` : ""}
${formData.publicLocation ? `Public Location: ${formData.publicLocation}` : ""}
${formData.publicCategory ? `Public Category: ${formData.publicCategory}` : ""}
Date: ${formData.date}
Time: ${formData.time}
Location: ${formData.location}
${formData.guestCount ? `Guest Count: ${formData.guestCount}` : ""}
${formData.powerAccess ? `Power Access: ${formData.powerAccess}` : ""}

Contact Information
-------------------
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}

${formData.notes ? `Additional Notes:\n${formData.notes}` : ""}

---
This request was submitted via the website booking form.
    `.trim();

    const buildDateTimeIso = (dateStr: string, minutes: number) => {
      const [year, month, day] = dateStr.split("-").map(Number);
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return new Date(year, month - 1, day, hours, mins).toISOString();
    };

    if (opsApiEnabled) {
      try {
        if (turnstileEnabled && !turnstileToken) {
          showToast({
            title: "Please verify",
            description: "Complete the Turnstile check before submitting.",
            variant: "warning",
          });
          return;
        }
        setStatus("sending");
        setApiErrorMessage(null);
        const startMinutes = parseTimeToMinutes(startTime) ?? 0;
        const endMinutes = parseTimeToMinutes(endTime) ?? startMinutes + 60;
        const notes = [
          `Event Type: ${formData.eventType}`,
          `Public Listing: ${formData.publicVisibility || "private"}`,
          formData.publicTitle ? `Public Title: ${formData.publicTitle}` : "",
          formData.publicDescription ? `Public Description: ${formData.publicDescription}` : "",
          formData.publicLocation ? `Public Location: ${formData.publicLocation}` : "",
          formData.publicCategory ? `Public Category: ${formData.publicCategory}` : "",
          `Date: ${formData.date}`,
          `Time: ${formData.time}`,
          `Location: ${formData.location}`,
          `Guest Count: ${formData.guestCount ?? ""}`,
          `Power Access: ${formData.powerAccess ?? ""}`,
          formData.phone ? `Phone: ${formData.phone}` : "",
          formData.notes ? `Notes: ${formData.notes}` : "",
        ]
          .filter(Boolean)
          .join("\n");

        const payload = {
          customerName: formData.name,
          customerEmail: formData.email,
          requestedStart: buildDateTimeIso(formData.date || "", startMinutes),
          requestedEnd: buildDateTimeIso(formData.date || "", endMinutes),
          notes,
          eventType: formData.eventType,
          location: formData.location,
          guestCount: formData.guestCount,
          powerAccess: formData.powerAccess,
          publicVisibility: formData.publicVisibility,
          publicTitle: formData.publicTitle,
          publicDescription: formData.publicDescription,
          publicLocation: formData.publicLocation,
          publicCategory: formData.publicCategory,
          businessSlug: opsApiConfig?.businessSlug,
          turnstileToken: turnstileToken ?? undefined,
        };

        const response = await fetch(`${opsApiConfig?.baseUrl}/api/v1/event-requests`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          setSubmitted(true);
          setStatus("idle");
          if (turnstileWidgetIdRef.current && (window as any).turnstile) {
            (window as any).turnstile.reset(turnstileWidgetIdRef.current);
            setTurnstileToken(null);
          }
          setTurnstileNonce((value) => value + 1);
          showToast({
            title: "Request sent",
            description: "Thanks! We'll reply about your event soon.",
            variant: "success",
          });
          return;
        }

        const errorPayload = (await response.json().catch(() => null)) as
          | { message?: string }
          | null;
        setApiErrorMessage(errorPayload?.message || "Unable to submit your request.");
        showToast({
          title: "Scheduling API unavailable",
          description: "Sending your request via email instead.",
          variant: "warning",
        });
      } catch (err) {
        console.error("Ops API request failed", err);
        setApiErrorMessage(
          err instanceof Error ? err.message : "Unable to submit your request."
        );
        showToast({
          title: "Scheduling API unavailable",
          description: "Sending your request via email instead.",
          variant: "warning",
        });
      }
    }

    // If EmailJS is configured, send via EmailJS; otherwise fall back to mailto
    if (emailJsConfig) {
      try {
        setStatus("sending");
        const emailjs = await import("@emailjs/browser");
        await emailjs.send(
          emailJsConfig.serviceId,
          emailJsConfig.templateId,
          {
            ...formData,
            subject,
            body,
            name: formData.name,
            email: formData.email,
            from_name: formData.name,
            from_email: formData.email,
            reply_to: formData.email,
            to_email: emailTo,
            message: body,
          },
          emailJsConfig.publicKey
        );
        setSubmitted(true);
        setStatus("idle");
        showToast({
          title: "Request sent",
          description: "Thanks! We'll reply about your event soon.",
          variant: "success",
        });
      } catch (err) {
        console.error("EmailJS send failed", err);
        setStatus("error");
        setApiErrorMessage(
          err instanceof Error ? err.message : "Unable to send your request."
        );
        showToast({
          title: "Unable to send request",
          description: "Please try again or call us directly.",
          variant: "error",
        });
      }
    } else {
      const mailtoLink = `mailto:${emailTo}?subject=${encodeURIComponent(
        subject
      )}&body=${encodeURIComponent(body)}`;
      window.location.href = mailtoLink;
      setSubmitted(true);
      showToast({
        title: "Email draft opened",
        description: "Send the email to finish your booking request.",
        variant: "info",
      });
    }
  };

  const textColor = "var(--color-text, #0f172a)";
  const mutedStyle = { color: textColor, opacity: 0.75 };
  const inputClass =
    "w-full px-4 py-2 rounded-[12px] border border-[color:var(--color-text,#0f172a)]/18 bg-[var(--color-surface-elevated,var(--color-surface,#fff))] text-[var(--color-text,#0f172a)] placeholder:text-[color:var(--color-text,#0f172a)]/60 focus:ring-2 focus:ring-primary focus:border-transparent";

  const renderSimpleForm = () => (
    <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Event Type */}
        <div>
          <label htmlFor="eventType" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
            Event Type *
          </label>
          <select
            id="eventType"
            required
            value={formData.eventType}
            onChange={(e) => updateField("eventType", e.target.value)}
            className={inputClass}
          >
            {eventTypes.map((type, index) => (
              <option key={index} value={type.label}>
                {type.label}
              </option>
            ))}
          </select>
          {eventTypes.find((t) => t.label === formData.eventType)?.description && (
            <p className="text-xs mt-1" style={mutedStyle}>
              {eventTypes.find((t) => t.label === formData.eventType)?.description}
            </p>
          )}
        </div>

        {/* Public listing preference */}
        <div className="md:col-span-2">
          <label className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
            Should this appear on the public events calendar?
          </label>
          <div className="flex flex-wrap gap-3">
            {[
              { value: "public", label: "Yes, public event" },
              { value: "private", label: "No, private event" },
              { value: "unsure", label: "Not sure" },
            ].map((option) => (
              <label
                key={option.value}
                className={cn(
                  "flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold cursor-pointer",
                  formData.publicVisibility === option.value
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-[color:var(--color-text,#0f172a)]/20 text-[color:var(--color-text,#0f172a)]/80"
                )}
              >
                <input
                  type="radio"
                  name="publicVisibility"
                  value={option.value}
                  checked={formData.publicVisibility === option.value}
                  onChange={(e) => updateField("publicVisibility", e.target.value)}
                  className="h-4 w-4"
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>

        {formData.publicVisibility === "public" && (
          <>
            <div className="md:col-span-2">
              <label htmlFor="publicTitle" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
                Public listing title (optional)
              </label>
              <input
                id="publicTitle"
                type="text"
                placeholder="Downtown Parkersburg Mobile Route"
                value={formData.publicTitle || ""}
                onChange={(e) => updateField("publicTitle", e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="publicDescription" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
                Public description (optional)
              </label>
              <textarea
                id="publicDescription"
                rows={3}
                placeholder="Add a short note for the public calendar."
                value={formData.publicDescription || ""}
                onChange={(e) => updateField("publicDescription", e.target.value)}
                className={cn(inputClass, "resize-none")}
              />
            </div>
            <div>
              <label htmlFor="publicLocation" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
                Public location label (optional)
              </label>
              <input
                id="publicLocation"
                type="text"
                placeholder="Downtown Parkersburg"
                value={formData.publicLocation || ""}
                onChange={(e) => updateField("publicLocation", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="publicCategory" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
                Public category label (optional)
              </label>
              <input
                id="publicCategory"
                type="text"
                placeholder="Mobile Route"
                value={formData.publicCategory || ""}
                onChange={(e) => updateField("publicCategory", e.target.value)}
                className={inputClass}
              />
            </div>
          </>
        )}

        {/* Date */}
        <div>
          <label htmlFor="date" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
            Preferred Date *
          </label>
          <input
            type="date"
            id="date"
            required
            value={formData.date || ""}
            onChange={(e) => updateField("date", e.target.value)}
            min={new Date().toISOString().split("T")[0]}
            className={inputClass}
          />
        </div>

        {/* Time - Start */}
        <div>
          <label htmlFor="startTime" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
            Start Time *
          </label>
          <select
            id="startTime"
            required
            value={startTime}
            onChange={(e) => {
              setStartTime(e.target.value);
              updateTime(e.target.value, endTime);
            }}
            className={inputClass}
          >
            <option value="">Select start time</option>
            {timeOptions.map((time, index) => (
              <option key={index} value={time}>
                {time}
              </option>
            ))}
          </select>
        </div>

        {/* Time - End */}
        <div>
          <label htmlFor="endTime" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
            End Time *
          </label>
          <select
            id="endTime"
            required
            value={endTime}
            onChange={(e) => {
              setEndTime(e.target.value);
              updateTime(startTime, e.target.value);
            }}
            className={inputClass}
          >
            <option value="">Select end time</option>
            {timeOptions.map((time, index) => (
              <option key={index} value={time}>
                {time}
              </option>
            ))}
          </select>
        </div>

        {/* Location */}
        <div>
          <label htmlFor="location" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
            Event Location *
          </label>
          <input
            type="text"
            id="location"
            required
            placeholder="Address or venue name"
            value={formData.location || ""}
            onChange={(e) => updateField("location", e.target.value)}
            className={inputClass}
          />
        </div>

        {/* Power Access */}
        <div>
          <label htmlFor="powerAccess" className="block text-sm font-semibold mb-2">
            Power Access On Site *
          </label>
          <select
            id="powerAccess"
            required
            value={formData.powerAccess || ""}
            onChange={(e) => updateField("powerAccess", e.target.value)}
            className={inputClass}
          >
            <option value="">Select an option</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
            <option value="Not sure">Not sure</option>
          </select>
        </div>

        {/* Guest Count */}
        <div>
          <label htmlFor="guestCount" className="block text-sm font-semibold mb-2">
            Expected Guest Count *
          </label>
          <input
            type="number"
            id="guestCount"
            min="1"
            required
            placeholder="Approximate number"
            value={formData.guestCount || ""}
            onChange={(e) => updateField("guestCount", parseInt(e.target.value) || 0)}
            className={inputClass}
          />
        </div>
      </div>

      {/* Contact Information */}
      <div className="border-t border-neutral-200 pt-6">
        <h3 className="text-lg font-bold mb-4" style={{ color: textColor }}>
          Your Contact Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="name" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
              Name *
            </label>
            <input
              type="text"
              id="name"
              required
              value={formData.name || ""}
              onChange={(e) => updateField("name", e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
              Email *
            </label>
            <input
              type="email"
              id="email"
              required
              value={formData.email || ""}
              onChange={(e) => updateField("email", e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
              Phone *
            </label>
            <input
              type="tel"
              id="phone"
              required
              value={formData.phone || ""}
              onChange={(e) => updateField("phone", e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label htmlFor="notes" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
          Additional Notes or Special Requests
        </label>
        <textarea
          id="notes"
          rows={4}
          placeholder="Tell us more about your event..."
          value={formData.notes || ""}
          onChange={(e) => updateField("notes", e.target.value)}
          className={cn(inputClass, "resize-none")}
        />
      </div>

      {turnstileEnabled && (
        <div>
          <div ref={turnstileContainerRef} />
          <p className="text-xs mt-2 text-slate-500">
            This site is protected by Turnstile to prevent spam.
          </p>
        </div>
      )}

      {/* Submit */}
      <div className="flex justify-center">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={status === "sending"}
          loadingText="Sending..."
        >
          {ctaText}
        </Button>
      </div>
    </form>
  );

  const renderFunnelForm = () => {
    const totalSteps = 3;
    // TODO: availability validation could be added by passing per-day busy windows and checking requested date/time.

    return (
      <div className="space-y-8">
        {/* Progress Bar */}
        <div className="flex items-center justify-between mb-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all",
                  s < step && "bg-primary text-white",
                  s === step && "bg-primary text-white ring-4 ring-primary/20",
                  s > step && "border-2 border-primary text-primary bg-white dark:bg-surface"
                )}
              >
                {s}
              </div>
              {s < totalSteps && (
                <div
                  className={cn(
                    "flex-1 h-1 mx-2 transition-all",
                    s < step ? "bg-primary" : "bg-primary/20"
                  )}
                />
              )}
            </div>
          ))}
        </div>
        {/* Progress track behind dots */}
        <div className="h-1 w-full rounded-full bg-[color:var(--color-text,#0f172a)]/12 mt-2 mb-4 relative">
          <div
            className="absolute h-1 rounded-full bg-primary transition-all"
            style={{ width: `${((step - 1) / (totalSteps - 1)) * 100}%` }}
          />
        </div>

        {/* Step Content */}
        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <div className="space-y-6" ref={stepTopRef}>
              <h3 className="text-xl font-bold mb-4" style={{ color: textColor }}>
                Event Details
              </h3>

              {/* Event Type Selection */}
              <div>
                <label className="block text-sm font-semibold mb-3" style={{ color: textColor }}>
                  What type of event do you need us for? *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {eventTypes.map((type, index) => {
                    const isIconName = type.icon && iconNames.includes(type.icon as IconName);
                    return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => updateField("eventType", type.label)}
                      className={cn(
                        "p-4 border-2 rounded-[14px] text-left transition-all",
                        formData.eventType === type.label
                          ? "border-primary bg-primary/5"
                          : cn(
                              "border-[color:var(--color-text,#0f172a)]/20 hover:border-primary/50",
                              eventErrors.eventType && "border-red-500 ring-1 ring-red-500/50"
                            )
                      )}
                    >
                      {type.icon && (
                        <AnimatedIcon variant="scale" className="h-12 w-12 mb-2 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                          {isIconName ? (
                            <Icon name={type.icon as IconName} className="h-6 w-6" />
                          ) : (
                            <span className="text-sm font-semibold">{type.icon}</span>
                          )}
                        </AnimatedIcon>
                      )}
                      <div className="font-semibold" style={{ color: textColor }}>
                        {type.label}
                      </div>
                      {type.description && (
                        <div className="text-xs mt-1" style={mutedStyle}>
                          {type.description}
                        </div>
                      )}
                    </button>
                  )})}
                </div>
                {eventErrors.eventType && (
                  <p className="text-sm text-red-500 mt-2">Please choose an event type.</p>
                )}
              </div>

              {/* Date & Time */}
              <div>
                <label htmlFor="date-funnel" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
                  Preferred Date *
                </label>
                <input
                  type="date"
                  id="date-funnel"
                  required
                  value={formData.date || ""}
                  onChange={(e) => updateField("date", e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className={cn(inputClass, eventErrors.date && "ring-2 ring-red-500 border-red-500")}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="startTime-funnel" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
                    Start Time *
                  </label>
                  <select
                    id="startTime-funnel"
                    required
                    value={startTime}
                    onChange={(e) => {
                      setStartTime(e.target.value);
                      setEventErrors((prev) => ({ ...prev, time: false }));
                      updateTime(e.target.value, endTime);
                    }}
                    className={cn(inputClass, eventErrors.time && "ring-2 ring-red-500 border-red-500")}
                  >
                    <option value="">Select start time</option>
                    {timeOptions.map((time, index) => (
                      <option key={index} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="endTime-funnel" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
                    End Time *
                  </label>
                  <select
                    id="endTime-funnel"
                    required
                    value={endTime}
                    onChange={(e) => {
                      setEndTime(e.target.value);
                      setEventErrors((prev) => ({ ...prev, time: false }));
                      updateTime(startTime, e.target.value);
                    }}
                    className={cn(inputClass, eventErrors.time && "ring-2 ring-red-500 border-red-500")}
                  >
                    <option value="">Select end time</option>
                    {timeOptions.map((time, index) => (
                      <option key={index} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Location */}
              <div>
                <label htmlFor="location-funnel" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
                  Event Location *
                </label>
                <input
                  type="text"
                  id="location-funnel"
                  required
                  placeholder="Address or venue name"
                  value={formData.location || ""}
                  onChange={(e) => updateField("location", e.target.value)}
                  className={cn(inputClass, eventErrors.location && "ring-2 ring-red-500 border-red-500")}
                />
              </div>

              {/* Power Access */}
              <div>
                <label htmlFor="powerAccess-funnel" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
                  Power Access On Site *
                </label>
                <select
                  id="powerAccess-funnel"
                  required
                  value={formData.powerAccess || ""}
                  onChange={(e) => updateField("powerAccess", e.target.value)}
                  className={cn(inputClass, eventErrors.powerAccess && "ring-2 ring-red-500 border-red-500")}
                >
                  <option value="">Select an option</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                  <option value="Not sure">Not sure</option>
                </select>
              </div>

              {/* Guest Count */}
              <div>
                <label htmlFor="guestCount-funnel" className="block text-sm font-semibold mb-2" style={{ color: textColor }}>
                  Expected Guest Count *
                </label>
                <input
                  type="number"
                  id="guestCount-funnel"
                  min="1"
                  required
                  placeholder="Approximate number"
                  value={formData.guestCount || ""}
                  onChange={(e) => updateField("guestCount", parseInt(e.target.value) || 0)}
                  className={cn(inputClass, eventErrors.guestCount && "ring-2 ring-red-500 border-red-500")}
                />
              </div>

              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={() => {
                    const nextErrors = buildEventErrors();
                    setEventErrors(nextErrors);

                    const invalidFields = Object.entries(nextErrors)
                      .filter(([, invalid]) => invalid)
                      .map(([field]) => field);

                    const timeOrderInvalid = startTime && endTime && !isTimeRangeValid();

                    if (invalidFields.length > 0 || timeOrderInvalid) {
                      const messages: Record<string, string> = {
                        eventType: "Select an event type.",
                        date: "Add a date.",
                        time: timeOrderInvalid ? "End time must be after start time." : "Add a start and end time.",
                        location: "Add a location.",
                        guestCount: "Guest count must be at least 1.",
                        powerAccess: "Select power access.",
                      };
                      showToast({
                        title: "Complete event details",
                        description: invalidFields.map((f) => messages[f]).join(" "),
                        variant: "error",
                      });
                      return;
                    }

                    if (requestConflictsWithAvailability()) {
                      showToast({
                        title: "Time not available",
                        description: "This date/time is booked. Please choose another window.",
                        variant: "error",
                      });
                      return;
                    }

                    setStep(2);
                  }}
                  variant="primary"
                  size="lg"
                >
                  Next Step →
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6" ref={stepTopRef}>
              <h3 className="text-xl font-bold mb-4">Your Contact Information</h3>

              <div>
                <label htmlFor="name-funnel" className="block text-sm font-semibold mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name-funnel"
                  required
                  value={formData.name || ""}
                  onChange={(e) => updateField("name", e.target.value)}
                  className={cn(inputClass, contactErrors.name && "ring-2 ring-red-500 border-red-500")}
                />
              </div>

              <div>
                <label htmlFor="email-funnel" className="block text-sm font-semibold mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email-funnel"
                  required
                  value={formData.email || ""}
                  onChange={(e) => updateField("email", e.target.value)}
                  className={cn(inputClass, contactErrors.email && "ring-2 ring-red-500 border-red-500")}
                />
              </div>

              <div>
                <label htmlFor="phone-funnel" className="block text-sm font-semibold mb-2">
                  Phone *
                </label>
                <input
                  type="tel"
                  id="phone-funnel"
                  required
                  value={formData.phone || ""}
                  onChange={(e) => updateField("phone", e.target.value)}
                  className={cn(inputClass, contactErrors.phone && "ring-2 ring-red-500 border-red-500")}
                />
              </div>

              <div className="flex justify-between">
                <Button
                  type="button"
                  onClick={() => setStep(1)}
                  variant="outline"
                  size="lg"
                >
                  ← Back
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    const nextErrors = buildContactErrors();
                    setContactErrors(nextErrors);

                    const invalidFields = Object.entries(nextErrors)
                      .filter(([, invalid]) => invalid)
                      .map(([field]) => field);

                    if (invalidFields.length > 0) {
                      const messages: Record<string, string> = {
                        name: "Name is required.",
                        email: "Email must be a valid address.",
                        phone: "Phone must be a valid number.",
                      };
                      showToast({
                        title: "Fix contact details",
                        description: invalidFields.map((f) => messages[f]).join(" "),
                        variant: "error",
                      });
                      return;
                    }

                    setStep(3);
                  }}
                  variant="primary"
                  size="lg"
                >
                  Next Step →
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6" ref={stepTopRef}>
              <h3 className="text-xl font-bold mb-4">Additional Details (Optional)</h3>

              <div>
                <label htmlFor="notes-funnel" className="block text-sm font-semibold mb-2">
                  Tell us more about your event
                </label>
                <textarea
                  id="notes-funnel"
                  rows={6}
                  placeholder="Any special requests, dietary restrictions, theme details, or other information we should know..."
                  value={formData.notes || ""}
                  onChange={(e) => updateField("notes", e.target.value)}
                  className={cn(inputClass, "resize-none")}
                />
              </div>

              {/* Review Summary */}
              <div className="bg-surface-alt p-6 rounded-[14px] border border-neutral-200">
                <h4 className="font-bold mb-3">Review Your Request</h4>
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="font-semibold text-neutral-600">Event Type:</dt>
                    <dd>{formData.eventType}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold text-neutral-600">Public listing:</dt>
                    <dd>{formData.publicVisibility || "private"}</dd>
                  </div>
                  {formData.publicVisibility === "public" && formData.publicTitle && (
                    <div>
                      <dt className="font-semibold text-neutral-600">Public title:</dt>
                      <dd>{formData.publicTitle}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="font-semibold text-neutral-600">Date & Time:</dt>
                    <dd>
                      {formData.date} {formData.time ? `at ${formData.time}` : ""}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-semibold text-neutral-600">Location:</dt>
                    <dd>{formData.location}</dd>
                  </div>
                  {formData.guestCount && (
                    <div>
                      <dt className="font-semibold text-neutral-600">Guest Count:</dt>
                      <dd>{formData.guestCount}</dd>
                    </div>
                  )}
                  {formData.powerAccess && (
                    <div>
                      <dt className="font-semibold text-neutral-600">Power Access:</dt>
                      <dd>{formData.powerAccess}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="font-semibold text-neutral-600">Contact:</dt>
                    <dd>
                      {formData.name} • {formData.email} • {formData.phone}
                    </dd>
                  </div>
                </dl>
              </div>

              {turnstileEnabled && (
                <div>
                  <div ref={turnstileContainerRef} />
                  <p className="text-xs mt-2 text-slate-500">
                    This site is protected by Turnstile to prevent spam.
                  </p>
                </div>
              )}

              <div className="flex justify-between">
                <Button
                  type="button"
                  onClick={() => {
                    if (
                      !formData.eventType ||
                      !formData.date ||
                      !formData.time ||
                      !formData.location ||
                      !isGuestCountValid ||
                      !formData.powerAccess
                    ) {
                      showToast({
                        title: "Complete event details",
                        description: "Please add date, time, location, guest count, and power access.",
                        variant: "error",
                      });
                      return;
                    }
                    setStep(2);
                  }}
                  variant="outline"
                  size="lg"
                >
                  ← Back
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={status === "sending"}
                  loadingText="Sending..."
                >
                  {ctaText}
                </Button>
              </div>
            </div>
          )}
        </form>
      </div>
    );
  };

  return (
    <BandedSection variant={bandVariant} className="py-16 md:py-24" contentClassName={className}>
      <div className="max-w-5xl mx-auto">
        {(title || subtitle) && (
          <div className="text-center mb-12">
            {title && <SectionTitle>{title}</SectionTitle>}
            {subtitle && <SectionSubtitle className="mt-4">{subtitle}</SectionSubtitle>}
            <p className="text-sm mt-3" style={{ color: textColor, opacity: 0.72 }}>
              {availabilityEvents.length > 0
                ? "Busy dates reflect current events. If your date is flexible, add a note."
                : "No dates are blocked yet. Share your ideal date and we will confirm availability."}
            </p>
          </div>
        )}

        <Card className="p-6 md:p-8 bg-[var(--color-surface,#fff)] text-[var(--color-text,#0f172a)] shadow-md-2 border border-[color:var(--color-text,#0f172a)]/10">
          <CardContent className="p-0">
            {submitted ? (
              <div className="text-center space-y-4">
                <Icon name="party" className="h-10 w-10 text-primary" />
                <h3 className="text-2xl font-bold" style={{ color: textColor }}>
                  Request Sent
                </h3>
                <p style={{ color: textColor, opacity: 0.75 }}>{successMessage}</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  {confirmationHref && (
                    <Button asChild variant="primary">
                      <a href={confirmationHref}>{confirmationLabel}</a>
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      setSubmitted(false);
                      setStatus("idle");
                      setStep(1);
                      setFormData({
                        eventType: eventTypes[0]?.label || "",
                        publicVisibility: "private",
                      });
                      setStartTime("");
                      setEndTime("");
                      if (turnstileWidgetIdRef.current && (window as any).turnstile) {
                        (window as any).turnstile.reset(turnstileWidgetIdRef.current);
                      }
                      setTurnstileToken(null);
                      turnstileWidgetIdRef.current = null;
                      setTurnstileNonce((value) => value + 1);
                    }}
                    variant={confirmationHref ? "outline" : "primary"}
                  >
                    Submit Another Request
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                {variant === "simple" ? renderSimpleForm() : renderFunnelForm()}
                {status === "error" && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    <p className="font-semibold">We couldn't send your request.</p>
                    <p className="mt-1">
                      {apiErrorMessage ||
                        "Please try again or reach us directly at " + emailTo + "."}
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BandedSection>
  );
}
